<template>
    <div aria-live="polite" class="visually-hidden">
        Sorted by {{ sortBy }}.
    </div>

    <div v-for="(year, yearIndex) in years" :key="yearIndex">
        <div class="performance-list__body">
            <div class="performance-list__months" v-for="(month, monthIndex) in year.months" :key="monthIndex">
                <div class="performance-list__month">
                    <h2 v-if="sortBy == 'date'"  class="performance-list__month-header">{{ month.name }}</h2>
                    <div class="performance-list__performances">
                        <template v-for="(day, dayIndex) in month.days" :key="dayIndex">
                            <template v-for="event in day.events" :key="event.id">
                                <ticket-list-event :event="event" />
                            </template>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { groupBy, map } from 'lodash';
import { format } from 'date-fns';
import { ref, defineComponent, watch, computed } from 'vue';
import { getState, useState } from '../services/stateful';

export default defineComponent({
    name: 'TicketsList',
    props: {
        items: {
            type: Array,
            default: () => [],
        }
    },
    setup(props) {
        const years = ref([]);
        const sortBy = useState('events.sort_by', 'date');

        const availabilityPriority = {
            'Good': 10,
            'Low': 20,
            'Limited': 30,
            'Sold Out': 100,
            'Cancelled': 150,
        };

        const computeAvailability = (items) => {
            if (!items.length) {
                years.value = [];
                return;
            }

            const sortedItems = [...items].sort((a, b) => {
                const aPriority = availabilityPriority[a.availability_level] || 999;
                const bPriority = availabilityPriority[b.availability_level] || 999;

                return aPriority - bPriority;
            });

            const groupedByAvailability = groupBy(sortedItems, item => item.availability_level);

            const availabilityGroups = map(groupedByAvailability, (events, availabilityLevel) => ({
                name: availabilityLevel,
                months: [{
                    name: availabilityLevel,
                    days: [{
                        date: new Date(),
                        events: events
                    }]
                }]
            }));

            years.value = availabilityGroups.sort((a, b) => {
                const aPriority = availabilityPriority[a.name] || 999;
                const bPriority = availabilityPriority[b.name] || 999;
                return aPriority - bPriority;
            });
        };

        const computeYearsMonthsDays = (items) => {
            if (!items.length) {
                years.value = [];
                return;
            }

            const groupedDays = groupBy(items, item => item._auxiliary.group_by_day);

            const processedDays = map(groupedDays, (dayEvents, key) => ({
                date: dayEvents[0].performancedate,
                events: dayEvents,
                _auxiliary: dayEvents[0]._auxiliary,
            }));

            const groupedMonths = groupBy(processedDays, day => day._auxiliary.group_by_month);

            const processedMonths = map(groupedMonths, (monthDays, monthKey) => {
                const firstDay = monthDays[0];
                return {
                    name: format(firstDay.date, 'MMMM'),
                    monthKey: monthKey,
                    days: monthDays,
                    year: format(firstDay.date, 'yyyy'),
                    _auxiliary: firstDay._auxiliary,
                };
            });

            const groupedYears = groupBy(processedMonths, month => month.year);

            years.value = map(groupedYears, (yearMonths, yearKey) => ({
                name: yearKey,
                months: yearMonths.map(month => ({
                    name: month.name,
                    days: month.days
                }))
            })).sort((a, b) => a.name.localeCompare(b.name));
        };

        const processItems = () => {
            if (sortBy.value === 'availability') {
                computeAvailability(props.items);
            } else {
                computeYearsMonthsDays(props.items);
            }
        };

        const formatDate = (date, formatStr) => {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            return format(dateObj, formatStr);
        };

        watch(
            () => props.items,
            () => {
                processItems();
            },
            { immediate: true }
        );

        watch(
            sortBy,
            () => {
                processItems();
            }
        );

        return {
            years,
            formatDate,
            sortBy
        };
    },
});
</script>
